import React, { useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import {
  Layout,
  Seo,
  Nav,
  HeaderContent,
  HowItWorks,
  HightLightSection,
  Cta,
  BookADemoButton,
  OverflowAlternatingFeatures,
} from "../../components"
import Icon from "../../components/icon"
import {
  title,
  subtitle,
  description,
  pageTitle,
  pageMetadata,
} from "../../data/casambi"
import { useOnScreen } from "../../utils/hooks"

export default function Casambi() {
  const ref = useRef()
  const isHeaderVisible = useOnScreen(ref)
  return (
    <Layout>
      <Seo title={pageTitle} description={pageMetadata} />
      <div>
        <Nav isHeaderVisible={isHeaderVisible} />
        <div className="relative bg-gray-900 overflow-hidden" ref={ref}>
          <div className="relative pt-6 pb-16 sm:pb-24 lg:pb-32">
            <HeaderContent
              title={{
                firstSentence: title,
                secondSentence: subtitle,
              }}
              description={
                <div className="sm:-mb-6">
                  {description.map(item => {
                    if (item.bold) {
                      return <h3 className="text-gray-50">{item.text}</h3>
                    } else {
                      return <p className="pb-6">{item.text}</p>
                    }
                  })}
                </div>
              }
              media={
                <StaticImage
                  src="../../assets/images/casambi/casambiHeader.png"
                  alt="Image of pushfusion interface on a mac book showing some Casambi devices."
                  placeholder="blurred"
                  layout="constrained"
                  quality={100}
                  height={430}
                  loading="eager"
                  aspectRatio={16 / 9}
                  className="rounded-lg"
                />
              }
              button={<BookADemoButton />}
            />
          </div>
        </div>

        <HowItWorks
          title={{ firstSentence: "How it", secondSentence: "works" }}
          position="middle"
          description={
            <>
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">
                fusion
              </span>{" "}
              harnesses the power of the Casambi API to bring data from across
              your estate together scaling to meet your demands as your Casambi
              networks grow.
            </>
          }
          image={
            <StaticImage
              src="../../assets/images/casambi/diagram.png"
              objectFit="cover"
              quality={100}
              width={1280}
              aspectRatio={4 / 3}
              placeholder="tracedSVG"
              alt="pushfusion Casambi cloud integration diagram"
            />
          }
        />

        <OverflowAlternatingFeatures
          title={{
            firstSentence: (
              <>
                <span className="text-gray-700 font-bold">push</span>
                <span className="text-pushfusion font-extrabold">
                  fusion
                </span>{" "}
              </>
            ),
            secondSentence: "dashboard",
          }}
          description={
            <>
              <span className="text-pushfusion-secondary font-bold">push</span>
              <span className="text-pushfusion font-extrabold">fusion </span>
              provides the tools that you need to understand and manage your
              estate compliance and proactively address any problems before they
              happen.
            </>
          }
          data={[
            {
              icon: (
                <Icon
                  className="h-16 w-16 text-blue-50"
                  aria-hidden="true"
                  color="#0E98FA"
                  icon="dashboard2"
                />
              ),
              title: "Secure cloud-based solution",
              content: (
                <>
                  <p>
                    Hosted by{" "}
                    <a
                      href="https://binaryforge.io?utm_source=pushfusion&utm_medium=referral&utm_id=link"
                      target="_blank"
                      rel="noreferrer"
                      className="corp"
                    >
                      Binary Forge
                    </a>
                    ,{" "}
                    <span className="text-pushfusion-secondary font-bold">
                      push
                    </span>
                    <span className="text-pushfusion font-extrabold">
                      fusion{" "}
                    </span>{" "}
                    has been developed from the start with security in its DNA,
                    the intuitive{" "}
                    <span className="text-pushfusion-secondary font-bold">
                      push
                    </span>
                    <span className="text-pushfusion font-extrabold">
                      fusion{" "}
                    </span>{" "}
                    interface allows you to access the data you need securely
                    and quickly from where you need it.
                  </p>
                  <p>
                    The Single portal provides access to your entire estate from
                    one place and contain features like proactive notification
                    of issues and events.
                  </p>
                </>
              ),
              image: (
                <StaticImage
                  src="../../assets/images/casambi/casambiDevices.png"
                  alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                  placeholder="blurred"
                  layout="constrained"
                  quality={100}
                  height={446}
                  loading="eager"
                  aspectRatio={16 / 9}
                  backgroundColor="transparent"
                  className="h-full rounded-xl shadow-xl"
                />
              ),
            },
            {
              icon: (
                <Icon
                  className="h-16 w-16 text-blue-50"
                  aria-hidden="true"
                  color="#0E98FA"
                  icon="report"
                />
              ),
              title: "Preset Reports",
              content: (
                <>
                  <p>
                    <span className="text-gray-900 font-bold">push</span>
                    <span className="text-pushfusion font-extrabold">
                      fusion
                    </span>{" "}
                    includes a suite of standard reports to assist you in your
                    daily decision making. Based on the data reported from your
                    Casambi installation they include historic energy, power and
                    temperature standalone reports as well as some useful
                    comparison reports to help you tease out extra value from
                    your hardware.
                  </p>
                  <p>
                    <p>
                      The Casambi API only holds your data for up to 60 days,
                      with <span className="text-gray-900 font-bold">push</span>
                      <span className="text-pushfusion font-extrabold">
                        fusion
                      </span>
                      , you can enjoy reports based on the lifetime of your
                      installation*.
                    </p>
                  </p>
                  <p className="text-sm">
                    *Only valid with an active pushfusion account /
                    subscription.
                  </p>
                </>
              ),
              image: (
                <StaticImage
                  src="../../assets/images/casambi/casambiReports.png"
                  alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                  placeholder="blurred"
                  layout="constrained"
                  quality={100}
                  height={446}
                  loading="eager"
                  aspectRatio={16 / 9}
                  backgroundColor="transparent"
                  className="h-full rounded-xl shadow-xl"
                />
              ),
            },
            {
              icon: (
                <Icon
                  className="h-16 w-16 text-blue-50"
                  aria-hidden="true"
                  color="#0E98FA"
                  icon="investigation"
                />
              ),
              title: "Alerting",
              content: (
                <>
                  <p>
                    The <span className="text-gray-900 font-bold">push</span>
                    <span className="text-pushfusion font-extrabold">
                      fusion
                    </span>{" "}
                    alerting engine gives you the power to set thresholds for
                    the behaviour of your installations. This means you can
                    monitor critical aspects of your infrastructure and receive
                    alerts when they start misbehaving.
                  </p>
                  <p>
                    This could help reduce your call-out overheads by up to 65%
                    because your maintenance teams will know what they’re
                    taking, where they’re going and what they’re fixing when
                    they get to site.
                  </p>
                </>
              ),
              image: (
                <StaticImage
                  src="../../assets/images/casambi/casambiDetails.png"
                  alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
                  placeholder="blurred"
                  layout="constrained"
                  quality={100}
                  height={446}
                  loading="eager"
                  aspectRatio={16 / 9}
                  backgroundColor="transparent"
                  className="h-full rounded-xl shadow-xl"
                />
              ),
            },
          ]}
        />
      </div>
      <HightLightSection
        image={
          <StaticImage
            src="../../assets/images/casambi/CasambiCustom.png"
            alt="Image of pushfusion interface on a mac book and pushfusion mobile app on an Iphone. Background shows commercial buildings."
            placeholder="blurred"
            layout="constrained"
            quality={100}
            loading="eager"
            className="w-full h-full object-center object-cover"
          />
        }
        title={["Flexible Custom", "Reporting Engine"]}
        subtitle="Not satisfied with the standard preset reports? No problem, with the custom reporting engine you can build the reports you want to see from your own data giving you the power and flexibility to pivot when your organisation needs it most."
      />
      <Cta
        title="See how pushfusion can"
        subtitle="supercharge your Casambi networks."
        button1={
          <BookADemoButton
            background="bg-gray-50"
            backgroundHover="bg-gray-300"
            textColor="text-pushfusion"
          />
        }
      />
    </Layout>
  )
}
